import React, { useEffect, useRef, useCallback, useState } from "react";
import * as d3 from "d3";
import "./MapTab.scss";
import Page_Loader from "../../../assets/vizr_images/page_loader.gif";
import { useSelector } from "react-redux";


const W = 800;
const H = 500;
const CX = W / 2;
const CY = H / 2;
const MIN_NODE_R = 18;
const MAX_NODE_R = 44;
const OWNER_R = 50;
const BLOOM_R = 165;

const COLORS = {
  owner: {
    fill: "#534AB7",
    ring: "#534AB7",
    badge: "#EEEDFE",
    badgeText: "#3C3489",
  },
  duplicate: {
    fill: "#D85A30",
    ring: "#D85A30",
    badge: "#FAECE7",
    badgeText: "#712B13",
  },
};


function getInitials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

function toUserRow(entry, ownerActorId) {
  return {
    id: `${entry.duplicateActor.id}-${entry.directory ?? "map"}-0`,
    username: entry.duplicateActor.username,
    email: entry.duplicateActor.email,
    directory: entry.directory ?? null,
    fileCount: entry.fileCount,
    duplicateActorId: entry.duplicateActor.id,
    actorId: ownerActorId,
  };
}


function computeLayout(content, ownerActorId, ownerUsername) {
  // Split self-duplicates from external duplicates
  const selfEntries = content.filter(
    (d) => d.duplicateActor.id === ownerActorId
  );
  const dupEntries = content.filter(
    (d) => d.duplicateActor.id !== ownerActorId
  );

  const selfCopyCount = selfEntries.reduce((s, d) => s + d.fileCount, 0);

  const maxCount = Math.max(...dupEntries.map((d) => d.fileCount), 1);

  function nodeR(fc) {
    return MIN_NODE_R + (fc / maxCount) * (MAX_NODE_R - MIN_NODE_R);
  }

  const periph = dupEntries.map((entry, i) => {
    const r = nodeR(entry.fileCount);
    const angle = ((2 * Math.PI) / dupEntries.length) * i - Math.PI / 2;
    return {
      id: entry.duplicateActor.id,
      username: entry.duplicateActor.username,
      email: entry.duplicateActor.email,
      fileCount: entry.fileCount,
      role: "duplicate",
      r,
      ox: Math.cos(angle) * BLOOM_R,
      oy: Math.sin(angle) * BLOOM_R,
      x: CX + Math.cos(angle) * BLOOM_R,
      y: CY + Math.sin(angle) * BLOOM_R,
      fx: CX + Math.cos(angle) * BLOOM_R,
      fy: CY + Math.sin(angle) * BLOOM_R,
      entry,
    };
  });

  const ownerFileCount = content.reduce((s, d) => s + d.fileCount, 0);

  // Use the first self-entry as the owner's "entry" for click handling,
  // or fall back to a synthetic one if there are no self entries
  const ownerEntry = selfEntries[0] ?? {
    duplicateActor: { id: "owner", username: ownerUsername, email: "" },
    fileCount: ownerFileCount,
    directory: null,
  };

  const ownerNode = {
    id: "owner",
    username: ownerUsername,
    email: ownerEntry.duplicateActor.email ?? "",
    fileCount: ownerFileCount,
    selfCopyCount,
    role: "owner",
    r: OWNER_R,
    ox: 0,
    oy: 0,
    x: CX,
    y: CY,
    fx: CX,
    fy: CY,
    entry: ownerEntry,
    // all self entries for click — pass first one (or aggregate as needed)
    selfEntries,
  };

  return { ownerNode, periph };
}


function buildGraph(container, content, drive, onUserClick) {
  d3.select(container).selectAll("*").remove();
  if (!content || content.length === 0) return;

  const ownerUsername = drive?.driveName ?? "";
  const ownerActorId = drive?.enterpriseActorId ?? null;

  const { ownerNode, periph } = computeLayout(content, ownerActorId, ownerUsername);
  const allNodes = [ownerNode, ...periph];

  const links = periph.map((n) => ({
    source: ownerNode,
    target: n,
    role: n.role,
  }));

  const svg = d3
    .select(container)
    .append("svg")
    .attr("width", "100%")
    .attr("height", "100%")
    .attr("viewBox", `0 0 ${W} ${H}`)
    .style("overflow", "hidden")
    .attr("aria-label", "Duplicate user relationship graph");

  const zoomBehaviour = d3
    .zoom()
    .scaleExtent([0.25, 4])
    .filter((event) => {
      if (event.type === "mousedown")
        return event.target === svg.node() || event.target.tagName === "svg";
      return true;
    })
    .on("zoom", (event) => root.attr("transform", event.transform));

  svg.call(zoomBehaviour);

  container.__zoomIn = () =>
    svg.transition().duration(280).call(zoomBehaviour.scaleBy, 1.4);
  container.__zoomOut = () =>
    svg.transition().duration(280).call(zoomBehaviour.scaleBy, 0.72);
  container.__reset = () =>
    svg.transition().duration(380).call(zoomBehaviour.transform, d3.zoomIdentity);

  svg.call(zoomBehaviour).on("dblclick.zoom", null);
  svg.on("dblclick", () => { container.__reset?.(); });

  const defs = svg.append("defs");

  defs
    .append("marker")
    .attr("id", "arr-dup")
    .attr("viewBox", "0 0 10 10")
    .attr("refX", 9).attr("refY", 5)
    .attr("markerWidth", 6).attr("markerHeight", 6)
    .attr("orient", "auto")
    .append("path")
    .attr("d", "M1 1L9 5L1 9")
    .attr("fill", "none")
    .attr("stroke", "#D85A30")
    .attr("stroke-width", "1.5")
    .attr("stroke-linecap", "round")
    .attr("stroke-linejoin", "round");

  const root = svg.append("g").attr("class", "graph-root");
  const edgeLayer = root.append("g").attr("class", "edges");
  const nodeLayer = root.append("g").attr("class", "nodes");

  function edgeCoords(link) {
    const sx = link.source.fx ?? link.source.x;
    const sy = link.source.fy ?? link.source.y;
    const tx = link.target.fx ?? link.target.x;
    const ty = link.target.fy ?? link.target.y;
    const dx = tx - sx;
    const dy = ty - sy;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    const ux = dx / dist;
    const uy = dy / dist;
    return {
      x1: sx + ux * (link.source.r + 6),
      y1: sy + uy * (link.source.r + 6),
      x2: tx - ux * (link.target.r + 12),
      y2: ty - uy * (link.target.r + 12),
    };
  }

  const edgeSel = edgeLayer
    .selectAll("line")
    .data(links)
    .enter()
    .append("line")
    .attr("stroke", "#D85A30")
    .attr("stroke-width", 1.2)
    .attr("opacity", 0.45)
    .attr("fill", "none")
    .attr("marker-end", "url(#arr-dup)");

  function renderEdges() {
    links.forEach((link) => { link._coords = edgeCoords(link); });
    edgeSel
      .attr("x1", (d) => d._coords.x1)
      .attr("y1", (d) => d._coords.y1)
      .attr("x2", (d) => d._coords.x2)
      .attr("y2", (d) => d._coords.y2);
  }

  const nodeGroups = nodeLayer
    .selectAll("g")
    .data(allNodes)
    .enter()
    .append("g")
    .attr("class", "node-g")
    .attr("transform", (d) => `translate(${d.fx},${d.fy})`);

  // Dashed ring for owner only
  nodeGroups
    .filter((d) => d.role === "owner")
    .append("circle")
    .attr("r", OWNER_R + 10)
    .attr("fill", "none")
    .attr("stroke", COLORS.owner.ring)
    .attr("stroke-width", 1.8)
    .attr("stroke-dasharray", "5 3")
    .attr("opacity", 0.4)
    .attr("class", "node-ring");

  // Main circle
  nodeGroups
    .append("circle")
    .attr("r", (d) => d.r)
    .attr("fill", (d) => COLORS[d.role].fill)
    .attr("class", "node-main");

  // Initials
  nodeGroups
    .append("text")
    .attr("text-anchor", "middle")
    .attr("dominant-baseline", "central")
    .attr("y", (d) => (d.role === "owner" ? -6 : 0))
    .attr("fill", "#ffffff")
    .attr("font-size", (d) => (d.role === "owner" ? 15 : 12))
    .attr("font-weight", "500")
    .attr("pointer-events", "none")
    .text((d) => getInitials(d.username));

  // Total copies sub-label inside owner node
  nodeGroups
    .filter((d) => d.role === "owner")
    .append("text")
    .attr("text-anchor", "middle")
    .attr("dominant-baseline", "central")
    .attr("y", 11)
    .attr("fill", "rgba(255,255,255,0.65)")
    .attr("font-size", 9)
    .attr("pointer-events", "none")
    .text((d) => `${d.fileCount} copies`);

  // Self-copy badge on owner node (top-right, purple toned)
  const ownerWithSelf = nodeGroups.filter(
    (d) => d.role === "owner" && d.selfCopyCount > 0
  );

  ownerWithSelf
    .append("circle")
    .attr("cx", OWNER_R * 0.72)
    .attr("cy", -OWNER_R * 0.72)
    .attr("r", 10)
    .attr("fill", COLORS.owner.badge)
    .attr("stroke", COLORS.owner.fill)
    .attr("stroke-width", 0.8);

  ownerWithSelf
    .append("text")
    .attr("x", OWNER_R * 0.72)
    .attr("y", -OWNER_R * 0.72)
    .attr("text-anchor", "middle")
    .attr("dominant-baseline", "central")
    .attr("fill", COLORS.owner.badgeText)
    .attr("font-size", 8)
    .attr("font-weight", "600")
    .attr("pointer-events", "none")
    .text((d) => d.selfCopyCount);

  // File-count badge on peripheral nodes
  const badgeG = nodeGroups.filter((d) => d.role === "duplicate");

  badgeG
    .append("circle")
    .attr("cx", (d) => d.r * 0.72)
    .attr("cy", (d) => -d.r * 0.72)
    .attr("r", 9)
    .attr("fill", COLORS.duplicate.badge)
    .attr("stroke", COLORS.duplicate.fill)
    .attr("stroke-width", 0.5);

  badgeG
    .append("text")
    .attr("x", (d) => d.r * 0.72)
    .attr("y", (d) => -d.r * 0.72)
    .attr("text-anchor", "middle")
    .attr("dominant-baseline", "central")
    .attr("fill", COLORS.duplicate.badgeText)
    .attr("font-size", 8)
    .attr("font-weight", "500")
    .attr("pointer-events", "none")
    .text((d) => d.fileCount);

  // Name label below node
  nodeGroups
    .append("text")
    .attr("text-anchor", "middle")
    .attr("dominant-baseline", "hanging")
    .attr("y", (d) => d.r + 10)
    .attr("fill", "var(--color-text-primary)")
    .attr("font-size", 11)
    .attr("font-weight", (d) => (d.role === "owner" ? "500" : "400"))
    .attr("pointer-events", "none")
    .text((d) => d.username.split(" ")[0]);

  // Hover interactions
  nodeGroups
    .on("mouseenter", function (_, d) {
      d3.select(this).select(".node-main")
        .transition().duration(140)
        .attr("r", d.r * 1.07)
        .attr("fill", d3.color(COLORS[d.role].fill).brighter(0.25));
      d3.select(this).select(".node-ring")
        .transition().duration(140)
        .attr("opacity", 0.7);
    })
    .on("mouseleave", function (_, d) {
      d3.select(this).select(".node-main")
        .transition().duration(140)
        .attr("r", d.r)
        .attr("fill", COLORS[d.role].fill);
      d3.select(this).select(".node-ring")
        .transition().duration(140)
        .attr("opacity", 0.4);
    });

  let dragMoved = false;

  const drag = d3
    .drag()
    .on("start", function (event) {
      dragMoved = false;
      event.sourceEvent.stopPropagation();
      d3.select(this).raise();
    })
    .on("drag", function (event, d) {
      dragMoved = true;
      if (d.role === "owner") {
        const dx = event.dx;
        const dy = event.dy;
        allNodes.forEach((n) => {
          n.fx = (n.fx ?? n.x) + dx;
          n.fy = (n.fy ?? n.y) + dy;
        });
        nodeGroups.attr("transform", (n) => `translate(${n.fx},${n.fy})`);
      } else {
        d.fx = event.x;
        d.fy = event.y;
        d3.select(this).attr("transform", `translate(${d.fx},${d.fy})`);
      }
      renderEdges();
    })
    .on("end", function (_, d) {
      if (!dragMoved) {
        // Both owner (self-copies) and peripheral duplicates fire onUserClick
        onUserClick(toUserRow(d.entry, ownerActorId));
      }
    });

  nodeGroups.call(drag);
  renderEdges();
  container.__stopSim = () => {};
}



export default function MapTab({ duplicateUserData, drive, onUserClick }) {
  const containerRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const ghostRef = useRef(null);
  const loader = useSelector((state) => state.dataTagReducer.popupLoader);

  // expandPhase: "closed" | "opening" | "open" | "closing"
  const [expandPhase, setExpandPhase] = useState("closed");
  const [originRect, setOriginRect] = useState(null);

  const handleUserClick = useCallback(
    (row) => { if (typeof onUserClick === "function") onUserClick(row); },
    [onUserClick],
  );

  useEffect(() => {
    if (!containerRef.current) return;
    buildGraph(
      containerRef.current,
      duplicateUserData?.content ?? [],
      drive,
      handleUserClick,
    );
    return () => containerRef.current?.__stopSim?.();
  }, [duplicateUserData, drive, handleUserClick]);

  // Build the graph again inside the overlay once it is mounted and the
  // FLIP animation has had a frame to start from the captured origin rect.
  useEffect(() => {
    if (expandPhase !== "opening" && expandPhase !== "open") return;
    if (!overlayCanvasRef.current) return;
    buildGraph(
      overlayCanvasRef.current,
      duplicateUserData?.content ?? [],
      drive,
      handleUserClick,
    );
    return () => overlayCanvasRef.current?.__stopSim?.();
  }, [expandPhase, duplicateUserData, drive, handleUserClick]);

  const openExpanded = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setOriginRect(rect);
    setExpandPhase("opening");
  }, []);

  // Drive the FLIP transition: ghost starts at the inline canvas's rect,
  // then on the next frame we animate it to the centered modal rect.
  useEffect(() => {
    if (expandPhase !== "opening" || !originRect || !ghostRef.current) return;

    const ghost = ghostRef.current;
    const targetW = Math.min(window.innerWidth * 0.86, 1100);
    const targetH = Math.min(window.innerHeight * 0.82, 760);
    const targetX = (window.innerWidth - targetW) / 2;
    const targetY = (window.innerHeight - targetH) / 2;

    // Start state (no transition) matching the inline canvas exactly.
    ghost.style.transition = "none";
    ghost.style.top = `${originRect.top}px`;
    ghost.style.left = `${originRect.left}px`;
    ghost.style.width = `${originRect.width}px`;
    ghost.style.height = `${originRect.height}px`;
    ghost.style.borderRadius = "0px";
    ghost.style.opacity = "1";

    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        ghost.style.transition =
          "top 0.32s cubic-bezier(0.22, 1, 0.36, 1), left 0.32s cubic-bezier(0.22, 1, 0.36, 1), width 0.32s cubic-bezier(0.22, 1, 0.36, 1), height 0.32s cubic-bezier(0.22, 1, 0.36, 1), border-radius 0.32s ease";
        ghost.style.top = `${targetY}px`;
        ghost.style.left = `${targetX}px`;
        ghost.style.width = `${targetW}px`;
        ghost.style.height = `${targetH}px`;
        ghost.style.borderRadius = "16px";
      });
    });

    const onEnd = () => setExpandPhase("open");
    ghost.addEventListener("transitionend", onEnd, { once: true });

    return () => {
      cancelAnimationFrame(raf);
      ghost.removeEventListener("transitionend", onEnd);
    };
  }, [expandPhase, originRect]);

  const closeExpanded = useCallback(() => {
    if (!ghostRef.current || !originRect) {
      setExpandPhase("closed");
      return;
    }
    const ghost = ghostRef.current;
    setExpandPhase("closing");

    requestAnimationFrame(() => {
      ghost.style.transition =
        "top 0.28s cubic-bezier(0.4, 0, 0.2, 1), left 0.28s cubic-bezier(0.4, 0, 0.2, 1), width 0.28s cubic-bezier(0.4, 0, 0.2, 1), height 0.28s cubic-bezier(0.4, 0, 0.2, 1), border-radius 0.28s ease, opacity 0.28s ease";
      ghost.style.top = `${originRect.top}px`;
      ghost.style.left = `${originRect.left}px`;
      ghost.style.width = `${originRect.width}px`;
      ghost.style.height = `${originRect.height}px`;
      ghost.style.borderRadius = "0px";
    });

    const onEnd = () => setExpandPhase("closed");
    ghost.addEventListener("transitionend", onEnd, { once: true });
  }, [originRect]);

  // Escape key closes the expanded popup
  useEffect(() => {
    if (expandPhase === "closed") return;
    const onKey = (e) => {
      if (e.key === "Escape") closeExpanded();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expandPhase, closeExpanded]);

  const hasData = (duplicateUserData?.content?.length ?? 0) > 0;
  const isExpandedVisible = expandPhase !== "closed";

  return (
    <div className="map-tab">
      {loader && (
        <img
          src={Page_Loader}
          alt="loading"
          className="loader-data-source dataTagPopupLoader"
        />
      )}
      {!hasData ? (
        <div className="map-tab__empty">No duplicate data available</div>
      ) : (
        <>
          <div className="map-tab__canvas" ref={containerRef} />
          <div className="map-tab__toolbar">
            <div className="map-tab__legend">
              <span className="map-tab__legend-item">
                <span className="map-tab__dot map-tab__dot--owner" />
                Drive owner
              </span>
              <span className="map-tab__legend-item">
                <span className="map-tab__dot map-tab__dot--dup" />
                Duplicate users
              </span>
            </div>
            <div className="map-tab__zoom-controls">
              <button
                className="map-tab__zoom-btn"
                title="Zoom in"
                onClick={() => containerRef.current?.__zoomIn?.()}
              >
                +
              </button>
              <button
                className="map-tab__zoom-btn"
                title="Zoom out"
                onClick={() => containerRef.current?.__zoomOut?.()}
              >
                −
              </button>
              <button
                className="map-tab__zoom-btn map-tab__zoom-btn--reset"
                title="Expand"
                onClick={openExpanded}
              >
                ⤢
              </button>
            </div>
          </div>
        </>
      )}

      {isExpandedVisible && (
        <div
          className={`map-tab__overlay-backdrop ${
            expandPhase === "open" ? "is-open" : ""
          }`}
          onClick={closeExpanded}
        >
          <div
            className="map-tab__overlay-ghost"
            ref={ghostRef}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="map-tab__overlay-close"
              title="Close"
              aria-label="Close expanded map"
              onClick={closeExpanded}
            >
              ×
            </button>
            <div className="map-tab__overlay-canvas" ref={overlayCanvasRef} />
            <div className="map-tab__overlay-toolbar">
              <div className="map-tab__legend">
                <span className="map-tab__legend-item">
                  <span className="map-tab__dot map-tab__dot--owner" />
                  Drive owner
                </span>
                <span className="map-tab__legend-item">
                  <span className="map-tab__dot map-tab__dot--dup" />
                  Duplicate users
                </span>
              </div>
              <div className="map-tab__zoom-controls">
                <button
                  className="map-tab__zoom-btn"
                  title="Zoom in"
                  onClick={() => overlayCanvasRef.current?.__zoomIn?.()}
                >
                  +
                </button>
                <button
                  className="map-tab__zoom-btn"
                  title="Zoom out"
                  onClick={() => overlayCanvasRef.current?.__zoomOut?.()}
                >
                  −
                </button>
                {!isExpandedVisible && (
                  <button
                    className="map-tab__zoom-btn"
                    title="Reset view"
                    onClick={() => overlayCanvasRef.current?.__reset?.()}
                  >
                    ⤢
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}